import { useState, useCallback } from 'react';
import { ProductLookupResult } from '../types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface UseProductLookupReturn {
  data: ProductLookupResult | null;
  loading: boolean;
  error: string | null;
  lookup: (sku: string) => Promise<void>;
  reset: () => void;
}

export function useProductLookup(): UseProductLookupReturn {
  const [data, setData] = useState<ProductLookupResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lookup = useCallback(async (sku: string) => {
    const trimmed = sku.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await fetch(`${API_URL}/api/products/sku/${encodeURIComponent(trimmed)}`);

      if (response.status === 404) {
        setError(`No product found for SKU: ${trimmed}`);
        return;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        setError(body?.error || `Server error (${response.status})`);
        return;
      }

      const result: ProductLookupResult = await response.json();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Network error — check connection');
    } finally {
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return { data, loading, error, lookup, reset };
}
